// El pedido de "Ambiente completo" al backend nuevo — `POST /v1/generate/ambiente-completo`
// (contrato: `docs/2026-09-27-ambiente-completo-contrato-front.md` de atlas-backend;
// ADR-2026-09-27-ambiente-completo-conectado).
//
// Es el ciclo de los orquestadores: la ruta valida, hace el gate de créditos y responde
// 202 con un `execution_id`; el resultado se sigue polleando `GET /v1/jobs/:id` hasta que
// el job termine. Devuelve los ids de `imagenes_generadas` de las escenas que salieron
// (una tanda con una escena fallida y otra bien es un éxito parcial: se muestran las que
// hay). Lanza `GenerateAdError` clasificado para que la columna de resultados decida qué
// decir —`billing` abre el modal de plan, `validation` muestra el mensaje del backend tal
// cual (dice qué tocar), el resto el copy neutro—.

import { atlasBackendFetch, pollJobUntilSettled } from '../../lib/atlasBackend';
import { GenerateAdError, mapErrorTypeToMessage, type GenerateAdErrorType } from '../../lib/generateAdRequest';
import type { RolProducto } from './ambienteCompleto';

export interface ProductoDelPedido {
  /** uuid de la variante (lo que la grilla llama producto). */
  id: string;
  nombre?: string | null;
  rol: RolProducto;
}

export interface PedidoAmbienteCompleto {
  productos: ProductoDelPedido[];
  /** Id de un ambiente curado; objeto cuando lo escribió el usuario o cuando es una foto
   *  propia del lugar (`file_name` de `fondos_personalizados`, sólo el nombre del archivo). */
  ambiente:
    | string
    | { tipo: 'propio'; texto: string }
    | { tipo: 'fondo'; file_name: string; nombre?: string | null };
  /** Escenas, 1..4. */
  cantidad: number;
  /** RATIO (`'3:2'`), no el id del formato: ver `formatSlugToAspect`. */
  formato: string;
  image_size?: '1K' | '2K' | '4K';
  instruccion?: string | null;
  /** La marca con la que se listaron los productos; sin ella el backend usa la activa. */
  brand_id?: string;
}

/** Cuánto se espera al job. El backend se rinde a los 10 minutos; acá un poco más. */
const POLL_TIMEOUT_MS = 11 * 60 * 1000;
const POLL_INTERVALO_MS = 3_000;

const TIPOS: ReadonlySet<string> = new Set<GenerateAdErrorType>([
  'policy_violation',
  'model_refusal',
  'billing',
  'provider_timeout',
  'technical',
  'validation',
]);

const tipoDe = (v: unknown): GenerateAdErrorType =>
  typeof v === 'string' && TIPOS.has(v) ? (v as GenerateAdErrorType) : 'technical';

export interface OpcionesDelPedido {
  /**
   * Se llama con el `execution_id` apenas el backend responde 202: la columna de
   * resultados lo anota en la tanda para poder reanudarla si se recarga la página
   * (ADR-2026-09-25-revision-greptile-la-tanda-aceptada-se-puede-reanudar).
   */
  onAceptado?: (executionId: string) => void;
}

export async function pedirAmbienteCompleto(
  body: PedidoAmbienteCompleto,
  opciones: OpcionesDelPedido = {},
): Promise<string[]> {
  let res: Response;
  try {
    res = await atlasBackendFetch('/v1/generate/ambiente-completo', body as unknown as Record<string, unknown>);
  } catch (e) {
    throw new GenerateAdError(
      'technical',
      mapErrorTypeToMessage('technical'),
      e instanceof Error ? e.message : 'network_error',
    );
  }
  const data: any = await res.json().catch(() => null);

  if (res.status === 402) {
    throw new GenerateAdError('billing', mapErrorTypeToMessage('billing'), data?.message ?? 'billing');
  }
  if (res.status === 400) {
    // El backend dice qué campo y qué hacer ("Mesa Oslo" no tiene fotos; la marca es de
    // otro rubro; hace falta un protagonista). Ese texto es el que se muestra.
    const msg = typeof data?.message === 'string' && data.message ? data.message : mapErrorTypeToMessage('validation');
    throw new GenerateAdError('validation', msg, data?.message ?? null);
  }
  if (res.status !== 202 || typeof data?.execution_id !== 'string') {
    throw new GenerateAdError(
      'technical',
      mapErrorTypeToMessage('technical'),
      data ? JSON.stringify(data) : `HTTP ${res.status}`,
    );
  }

  opciones.onAceptado?.(data.execution_id);

  const job = await pollJobUntilSettled(data.execution_id, {
    timeoutMs: POLL_TIMEOUT_MS,
    intervalMs: POLL_INTERVALO_MS,
  });
  if (!job) {
    throw new GenerateAdError('provider_timeout', mapErrorTypeToMessage('provider_timeout'), 'poll_timeout');
  }
  if (job.status === 'failed') {
    const tipo = tipoDe(job.errorType);
    throw new GenerateAdError(tipo, job.userMessage ?? mapErrorTypeToMessage(tipo), job.errorType);
  }
  const ids = job.output?.imagenes_generadas_ids;
  const lista = Array.isArray(ids) ? ids.map((v) => String(v)) : [];
  if (lista.length === 0) {
    throw new GenerateAdError('technical', mapErrorTypeToMessage('technical'), 'sin_imagenes');
  }
  return lista;
}
